/**
 * Product Carousel — product-carousel.js
 * Place in: /assets/product-carousel.js
 *
 * Physics engine: velocity → friction deceleration → spring snap
 * Mobile: active card detection → fade-in details
 */

(function () {
  'use strict';

  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  const FRICTION      = 0.92;
  const SPRING_K      = 0.18;
  const SPRING_DAMP   = 0.72;
  const MIN_VEL       = 0.3;
  const SWIPE_THRESH  = 40;

  class CarouselInstance {
    constructor(section, config) {
      this.id          = config.id;
      this.section     = section;
      this.config      = config;
      this.total       = config.total;
      this.currentIdx  = 0;

      this.track       = section.querySelector(`#pc-track-${this.id}`);
      this.fillEl      = section.querySelector(`#pc-fill-${this.id}`);
      this.countEl     = section.querySelector(`#pc-current-${this.id}`);
      this.prevBtn     = section.querySelector(`#pc-prev-${this.id}`);
      this.nextBtn     = section.querySelector(`#pc-next-${this.id}`);
      this.cards       = Array.from(this.track ? this.track.querySelectorAll('.pc-card') : []);

      if (!this.track || this.cards.length === 0) return;

      this.x           = 0;
      this.targetX     = 0;
      this.velX        = 0;
      this.rafId       = null;
      this.isPhysics   = false;
      this.isSnapping  = false;

      this.pointerDown = false;
      this.pointerStartX = 0;
      this.pointerLastX  = 0;
      this.pointerStartTime = 0;
      this.dragDelta   = 0;

      this.isMobile    = false;
      this.cardWidth   = 0;
      this.gap         = parseInt(config.gap, 10) || 16;

      this.autoplayTimer = null;

      this.init();
    }

    measure() {
      if (!this.cards[0]) return;

      this.track.style.gap = `${this.gap}px`;
      this.isMobile = window.innerWidth <= 749;

      if (!this.isMobile) {
        const cols     = parseInt(this.config.desktopCols, 10) || 3;
        const vpRect   = this.track.parentElement.getBoundingClientRect();
        const vpStyle  = window.getComputedStyle(this.track.parentElement);
        const paddingX = parseFloat(vpStyle.paddingLeft || 0) + parseFloat(vpStyle.paddingRight || 0);
        const availW   = vpRect.width - paddingX;
        const cw       = (availW - (cols - 1) * this.gap) / cols;

        this.cardWidth = cw;
        this.cards.forEach(c => {
          c.style.flex     = `0 0 ${cw}px`;
          c.style.maxWidth = `${cw}px`;
        });
      } else {
        this.cards.forEach(c => {
          c.style.flex     = '';
          c.style.maxWidth = '';
        });
        requestAnimationFrame(() => {
          if (this.cards[0]) {
            this.cardWidth = this.cards[0].getBoundingClientRect().width;
          }
        });
      }
    }

    snapX(idx) {
      const i       = clamp(idx, 0, this.total - 1);
      const cols    = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
      const step    = this.cardWidth + this.gap;
      const maxIdx  = Math.max(0, this.total - cols);
      const clampedI= clamp(i, 0, maxIdx);
      return -clampedI * step;
    }

    setX(x) {
      this.x = x;
      this.track.style.transform = `translateX(${x}px)`;
    }

    goTo(idx, animate = true) {
      const cols   = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
      const maxIdx = Math.max(0, this.total - cols);
      idx = clamp(idx, 0, maxIdx);
      this.currentIdx = idx;
      this.targetX    = this.snapX(idx);

      if (animate) {
        this.startSpring();
      } else {
        this.cancelPhysics();
        this.setX(this.targetX);
        this.updateUI();
      }
    }

    startSpring() {
      this.cancelPhysics();
      this.isPhysics = true;

      const tick = () => {
        if (!this.isPhysics) return;

        const dx    = this.targetX - this.x;
        const force = dx * SPRING_K;
        this.velX  = (this.velX + force) * SPRING_DAMP;
        this.setX(this.x + this.velX);

        const settled = Math.abs(dx) < 0.15 && Math.abs(this.velX) < 0.1;
        if (settled) {
          this.setX(this.targetX);
          this.isPhysics = false;
          this.updateUI();
          return;
        }
        this.rafId = requestAnimationFrame(tick);
      };

      this.rafId = requestAnimationFrame(tick);
    }

    startMomentum() {
      this.cancelPhysics();
      this.isPhysics = true;

      const tick = () => {
        if (!this.isPhysics) return;

        this.velX *= FRICTION;
        const proposed = this.x + this.velX;

        const minX = this.snapX(this.total - 1);
        const maxX = 0;
        let nx;
        if (proposed > maxX) {
          nx = maxX + (proposed - maxX) * 0.2;
          this.velX *= 0.3;
        } else if (proposed < minX) {
          nx = minX + (proposed - minX) * 0.2;
          this.velX *= 0.3;
        } else {
          nx = proposed;
        }
        this.setX(nx);

        if (Math.abs(this.velX) < MIN_VEL) {
          this.snapNearest();
          return;
        }
        this.rafId = requestAnimationFrame(tick);
      };

      this.rafId = requestAnimationFrame(tick);
    }

    snapNearest() {
      const step = this.cardWidth + this.gap;
      const cols = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
      let idx    = Math.round(-this.x / step);
      const max  = Math.max(0, this.total - cols);
      idx        = clamp(idx, 0, max);
      this.currentIdx = idx;
      this.targetX    = this.snapX(idx);
      this.isPhysics  = false;
      this.startSpring();
    }

    cancelPhysics() {
      this.isPhysics = false;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    updateUI() {
      const cols   = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
      const maxIdx = Math.max(0, this.total - cols);
      const pct    = maxIdx > 0 ? (this.currentIdx / maxIdx) * 100 : 100;

      if (this.fillEl)  this.fillEl.style.width  = `${pct}%`;
      if (this.countEl) this.countEl.textContent = this.currentIdx + 1;

      if (this.prevBtn) this.prevBtn.disabled = this.currentIdx === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIdx >= maxIdx;

      this.updateActiveCards();
    }

    updateActiveCards() {
      if (!this.isMobile) {
        this.cards.forEach(c => c.classList.add('is-active'));
        return;
      }
      const viewportCenter = this.track.parentElement.getBoundingClientRect().left
                            + this.track.parentElement.offsetWidth / 2;

      let closest = { idx: 0, dist: Infinity };
      this.cards.forEach((c, i) => {
        const r    = c.getBoundingClientRect();
        const mid  = r.left + r.width / 2;
        const dist = Math.abs(mid - viewportCenter);
        if (dist < closest.dist) closest = { idx: i, dist };
      });

      this.cards.forEach((c, i) => {
        c.classList.toggle('is-active', i === closest.idx);
      });
    }

    bindPointer() {
      const vp = this.track.parentElement;

      const onDown = (e) => {
        this.cancelPhysics();
        this.pointerDown      = true;
        this.pointerStartX    = e.touches ? e.touches[0].clientX : e.clientX;
        this.pointerLastX     = this.pointerStartX;
        this.pointerStartTime = Date.now();
        this.velX             = 0;
        this.dragDelta        = 0;
        this.stopAutoplay();
      };

      const onMove = (e) => {
        if (!this.pointerDown) return;
        const cx  = e.touches ? e.touches[0].clientX : e.clientX;
        const dx  = cx - this.pointerLastX;
        this.pointerLastX = cx;
        this.dragDelta    = cx - this.pointerStartX;
        this.velX         = dx;

        if (Math.abs(this.dragDelta) > 8 && e.cancelable) {
          e.preventDefault();
        }

        const proposed = this.x + dx;
        const minX     = this.snapX(this.total - 1);
        const maxX     = 0;
        if (proposed > maxX)       this.setX(maxX + (proposed - maxX) * 0.25);
        else if (proposed < minX)  this.setX(minX + (proposed - minX) * 0.25);
        else                       this.setX(proposed);

        const step   = this.cardWidth + this.gap;
        const cols   = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
        const maxIdx = Math.max(0, this.total - cols);
        const frac   = clamp(-this.x / (step * maxIdx), 0, 1);
        if (this.fillEl) this.fillEl.style.width = `${frac * 100}%`;
        this.updateActiveCards();
      };

      const onUp = () => {
        if (!this.pointerDown) return;
        this.pointerDown = false;

        const elapsed = Date.now() - this.pointerStartTime;
        const absD    = Math.abs(this.dragDelta);

        if (absD > SWIPE_THRESH) {
          const dir = this.dragDelta < 0 ? 1 : -1;
          this.goTo(this.currentIdx + dir);
        } else if (elapsed < 250 && absD > 20) {
          this.startMomentum();
        } else {
          this.targetX = this.snapX(this.currentIdx);
          this.startSpring();
        }

        this.startAutoplay();
      };

      vp.addEventListener('pointerdown', onDown, { passive: true });
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp, { passive: true });

      vp.addEventListener('touchstart', onDown, { passive: true });
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp, { passive: true });
    }

    bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.goTo(this.currentIdx - 1);
          this.resetAutoplay();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.goTo(this.currentIdx + 1);
          this.resetAutoplay();
        });
      }
    }

    bindKeyboard() {
      this.section.setAttribute('tabindex', '0');
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { this.goTo(this.currentIdx - 1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { this.goTo(this.currentIdx + 1); e.preventDefault(); }
      });
    }

    startAutoplay() {
      if (!this.config.autoplay) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        const cols   = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
        const maxIdx = Math.max(0, this.total - cols);
        const next   = this.currentIdx >= maxIdx ? 0 : this.currentIdx + 1;
        this.goTo(next);
      }, parseInt(this.config.autoplayMs, 10) || 4000);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    bindResize() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.isMobile = window.innerWidth <= 749;
          this.measure();
          const cols   = this.isMobile ? 1 : parseInt(this.config.desktopCols, 10);
          const maxIdx = Math.max(0, this.total - cols);
          if (this.currentIdx > maxIdx) this.currentIdx = maxIdx;
          this.goTo(this.currentIdx, false);
        }, 120);
      }, { passive: true });
    }

    bindVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) this.startAutoplay();
          else this.stopAutoplay();
        });
      }, { threshold: 0.1 });
      obs.observe(this.section);
    }

    init() {
      this.isMobile = window.innerWidth <= 749;

      requestAnimationFrame(() => {
        this.measure();
        this.goTo(0, false);
        this.bindPointer();
        this.bindArrows();
        this.bindKeyboard();
        this.bindResize();
        this.bindVisibility();
        this.startAutoplay();
      });
    }
  }

  function bootAll() {
    const configs = window.__pcConfig || {};
    Object.keys(configs).forEach((id) => {
      const cfg     = configs[id];
      const section = document.getElementById(`pc-${id}`);
      if (section && !section.__pcInit) {
        section.__pcInit = new CarouselInstance(section, cfg);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target;
      if (!section) return;
      setTimeout(() => {
        const id  = section.querySelector('[id^="pc-track-"]')?.id?.replace('pc-track-','');
        const cfg = id && window.__pcConfig?.[id];
        if (cfg && !section.querySelector('.pc-section')?.__pcInit) {
          const sEl = section.querySelector('.pc-section');
          if (sEl) sEl.__pcInit = new CarouselInstance(sEl, cfg);
        }
      }, 100);
    });
  }

})();